import api from './api';

/**
 * Service for interacting with merchant-related API endpoints
 */
const merchantService = {
  /**
   * Get public merchant information
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with public merchant data
   */
  getPublicMerchantInfo: (merchantId) => {
    return api.get(`/merchants/public/${merchantId}/`);
  },

  /**
   * Get a list of all merchants (admin only)
   * @returns {Promise} Promise object with merchants data
   */
  getMerchants: () => {
    return api.get('/merchants/');
  },

  /**
   * Get a specific merchant by ID
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with merchant data
   */
  getMerchant: (merchantId) => {
    return api.get(`/merchants/${merchantId}/`);
  },

  /**
   * Create a new merchant and assign current user as owner
   * @param {Object} merchantData - Merchant data
   * @returns {Promise} Promise object with created merchant data
   */
  createMerchant: (merchantData) => {
    return api.post('/merchants/my-merchant/', merchantData);
  },

  /**
   * Update a merchant
   * @param {string} merchantId - UUID of the merchant
   * @param {Object} merchantData - Updated merchant data
   * @returns {Promise} Promise object with updated merchant data
   */
  updateMerchant: (merchantId, merchantData) => {
    return api.put(`/merchants/${merchantId}/`, merchantData);
  },

  /**
   * Delete a merchant
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object
   */
  deleteMerchant: (merchantId) => {
    return api.delete(`/merchants/${merchantId}/`);
  },

  /**
   * Get settings for a merchant
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with merchant settings data
   */
  getMerchantSettings: (merchantId) => {
    return api.get(`/merchants/${merchantId}/settings/`);
  },

  /**
   * Request an email-confirmed change to the merchant's GCash payout number.
   * Sends a 6-digit code to the admin's email; the change is not applied yet.
   * @param {string} merchantId - UUID of the merchant
   * @param {string} gcashMobileNumber - The requested new GCash number
   * @returns {Promise} Promise resolving when the code has been sent
   */
  requestGcashNumberChange: (merchantId, gcashMobileNumber) => {
    return api.post(`/merchants/${merchantId}/settings/gcash-number/request/`, {
      gcash_mobile_number: gcashMobileNumber,
    });
  },

  /**
   * Confirm and apply a pending GCash payout number change with the emailed code.
   * @param {string} merchantId - UUID of the merchant
   * @param {string} code - The 6-digit confirmation code
   * @returns {Promise} Promise object with the updated settings data
   */
  confirmGcashNumberChange: (merchantId, code) => {
    return api.post(`/merchants/${merchantId}/settings/gcash-number/confirm/`, {
      code,
    });
  },

  /**
   * Update settings for a merchant
   * @param {string} merchantId - UUID of the merchant
   * @param {Object|FormData} settingsData - Updated settings data
   * @param {boolean} partial - Whether to use PATCH (partial update) or PUT (full update)
   * @returns {Promise} Promise object with updated settings data
   */
  updateMerchantSettings: (merchantId, settingsData, partial = true) => {
    console.log('updateMerchantSettings called with:', { merchantId, settingsData, partial });
    console.log('Is FormData:', settingsData instanceof FormData);

    let config = {};
    let requestData = settingsData;

    // Don't set Content-Type header for FormData - let browser set it with boundary
    if (settingsData instanceof FormData) {
      config.headers = {
        // Don't set Content-Type - browser will set it with boundary
      };
      console.log('Using FormData with config:', config);

      // Log FormData contents
      console.log('FormData contents:');
      for (let [key, value] of settingsData.entries()) {
        console.log(`${key}:`, value);
      }
    } else {
      // For JSON data, only filter out actual File objects, allow base64 strings
      const filteredData = { ...settingsData };

      // Only remove cover_photo if it's a File object, keep base64 strings
      if ('cover_photo' in filteredData && filteredData.cover_photo instanceof File) {
        console.log(`Removing File object from JSON payload:`, filteredData.cover_photo);
        delete filteredData.cover_photo;
      } else if ('cover_photo' in filteredData && typeof filteredData.cover_photo === 'string' && filteredData.cover_photo.startsWith('data:image/')) {
        console.log(`Keeping base64 cover_photo in JSON payload (length: ${filteredData.cover_photo.length})`);
      }

      requestData = filteredData;
      console.log('Using filtered JSON data:', requestData);
    }

    console.log('Making API call...');
    if (partial) {
      return api.patch(`/merchants/${merchantId}/settings/`, requestData, config);
    } else {
      return api.put(`/merchants/${merchantId}/settings/`, requestData, config);
    }
  },

  // UserMerchant related methods

  /**
   * Get user's merchants (from user profile - already available in auth context)
   * This is mainly for refreshing the data
   * @returns {Promise} Promise object with user's merchants
   */
  getUserMerchants: () => {
    return api.get('/users/me/');
  },

  /**
   * Get all user-merchant relationships for a specific merchant
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with user-merchant relationships
   */
  getMerchantUsers: (merchantId) => {
    return api.get(`/merchants/user-merchants/?merchant=${merchantId}`);
  },

  /**
   * Add a user to a merchant
   * @param {Object} data - User-merchant relationship data
   * @param {string} data.user_id - UUID of the user
   * @param {string} data.merchant_id - UUID of the merchant
   * @param {string} data.role - Role ('staff', 'manager', 'admin', 'owner')
   * @param {boolean} data.is_active - Whether the relationship is active
   * @returns {Promise} Promise object with created relationship
   */
  addUserToMerchant: (data) => {
    return api.post('/merchants/user-merchants/', data);
  },

  /**
   * Update a user's role in a merchant
   * @param {string} userMerchantId - UUID of the user-merchant relationship
   * @param {Object} data - Updated data
   * @param {string} data.role - New role
   * @param {boolean} data.is_active - Whether the relationship is active
   * @returns {Promise} Promise object with updated relationship
   */
  updateUserMerchantRole: (userMerchantId, data) => {
    return api.put(`/merchants/user-merchants/${userMerchantId}/`, data);
  },

  /**
   * Remove a user from a merchant (deactivate relationship)
   * @param {string} userMerchantId - UUID of the user-merchant relationship
   * @returns {Promise} Promise object
   */
  removeUserFromMerchant: (userMerchantId) => {
    return api.delete(`/merchants/user-merchants/${userMerchantId}/`);
  },

  /**
   * Get a specific user-merchant relationship
   * @param {string} userMerchantId - UUID of the user-merchant relationship
   * @returns {Promise} Promise object with relationship data
   */
  getUserMerchantRelationship: (userMerchantId) => {
    return api.get(`/merchants/user-merchants/${userMerchantId}/`);
  },

  /**
   * Get all user-merchant relationships (admin only)
   * @param {Object} params - Query parameters
   * @param {string} params.user - Filter by user ID
   * @param {string} params.merchant - Filter by merchant ID
   * @param {string} params.role - Filter by role
   * @param {boolean} params.is_active - Filter by active status
   * @returns {Promise} Promise object with relationships
   */
  getAllUserMerchantRelationships: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return api.get(`/merchants/user-merchants/${queryString ? '?' + queryString : ''}`);
  },

  /**
   * Get current user's merchant using the UserMerchant relationship
   * @returns {Promise} Promise object with current merchant data
   */
  getCurrentMerchant: async () => {
    try {
      // Try to get merchant via UserMerchant relationship first
      try {
        const response = await api.get('/merchants/my-merchant/');
        return response.data;
      } catch (userMerchantError) {
        console.log('UserMerchant endpoint failed, trying current merchant endpoint:', userMerchantError.response?.status);

        // If UserMerchant fails, try the current merchant endpoint (uses middleware)
        try {
          const response = await api.get('/merchants/current/');
          return response.data;
        } catch (currentMerchantError) {
          console.log('Current merchant endpoint failed, trying localStorage:', currentMerchantError.response?.status);

          // If both API endpoints fail, check localStorage as fallback
          const storedMerchant = localStorage.getItem('merchant');
          if (storedMerchant) {
            const parsedMerchant = JSON.parse(storedMerchant);
            if (parsedMerchant.name && parsedMerchant.id) {
              return parsedMerchant;
            }
          }

          // If all methods fail, throw the original error
          throw userMerchantError;
        }
      }
    } catch (error) {
      console.error('Error getting current merchant:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Update current user's merchant
   * @param {Object|FormData} merchantData - Updated merchant data
   * @returns {Promise} Promise object with updated merchant data
   */
  updateCurrentMerchant: async (merchantData) => {
    try {
      // Get current merchant first to get the ID
      const currentMerchant = await merchantService.getCurrentMerchant();

      // Try to update using the UserMerchant endpoint first (if it supports updates)
      let response;
      try {
        // Try PUT on my-merchant endpoint first
        response = await api.put('/merchants/my-merchant/', merchantData);
      } catch (userMerchantUpdateError) {
        console.log('UserMerchant update failed, trying PATCH on my-merchant:', userMerchantUpdateError.response?.status);

        // Try PATCH on my-merchant endpoint
        try {
          response = await api.patch('/merchants/my-merchant/', merchantData);
        } catch (patchError) {
          console.log('PATCH on my-merchant failed, trying direct merchant update:', patchError.response?.status);

          // Fallback to direct merchant update (might require admin permissions)
          response = await api.patch(`/merchants/${currentMerchant.id}/`, merchantData);
        }
      }

      // Update localStorage if it exists
      const storedMerchant = localStorage.getItem('merchant');
      if (storedMerchant) {
        const parsedMerchant = JSON.parse(storedMerchant);
        const updatedMerchant = { ...parsedMerchant, ...response.data };
        localStorage.setItem('merchant', JSON.stringify(updatedMerchant));
      }

      return response.data;
    } catch (error) {
      console.error('Error updating current merchant:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  /**
   * Switch to a different merchant
   * @param {string} merchantId - UUID of the merchant to switch to
   * @returns {Promise} Promise object with updated merchant data
   */
  switchMerchant: async (merchantId) => {
    try {
      const response = await api.post('/merchants/switch/', { merchant_id: merchantId });
      return response.data;
    } catch (error) {
      console.error('Error switching merchant:', error);
      console.error('Error details:', error.response?.data || error.message);
      throw error;
    }
  },

  // Status methods

  getMerchantStatus: (merchantId) => {
    return api.get(`/merchants/${merchantId}/status/`);
  },

  updateMerchantStatus: (merchantId, statusData) => {
    return api.post(`/merchants/${merchantId}/status/`, statusData);
  },

  // Business Hours methods

  getBusinessHours: (merchantId) => {
    return api.get(`/merchants/${merchantId}/business-hours/`);
  },

  updateBusinessHours: (merchantId, hoursData) => {
    return api.put(`/merchants/${merchantId}/business-hours/`, hoursData);
  },

  // Discount methods

  getDiscounts: (merchantId) => {
    return api.get(`/merchants/${merchantId}/discounts/`);
  },

  createDiscount: (merchantId, discountData) => {
    return api.post(`/merchants/${merchantId}/discounts/`, discountData);
  },

  updateDiscount: (merchantId, discountId, discountData) => {
    return api.patch(`/merchants/${merchantId}/discounts/${discountId}/`, discountData);
  },

  deleteDiscount: (merchantId, discountId) => {
    return api.delete(`/merchants/${merchantId}/discounts/${discountId}/`);
  },

  // Legacy methods for backward compatibility

  /**
   * @deprecated Use createMerchant instead
   * Create a new merchant for the current user
   */
  createMerchantAdmin: (merchantData) => {
    return this.createMerchant(merchantData);
  },

  /**
   * @deprecated Use getMerchant instead
   * Get a specific merchant by ID
   */
  getMerchantById: (merchantId) => {
    return this.getMerchant(merchantId);
  },

  /**
   * @deprecated Use getUserMerchants instead
   * Get the current user's merchant
   */
  getUserMerchant: () => {
    return this.getUserMerchants();
  }
};

export default merchantService;
